import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
// Fontawesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faPenToSquare,
  faEye,
  faArrowRotateRight,
} from "@fortawesome/free-solid-svg-icons";
// Components
import TableLoader from "../../../components/TableLoader";
import DropDown from "../../../components/form/DropDown";
import Pagination from "../../../components/Pagination";
import DateRangeCalendar from "../../../components/form/DateRangeCalendar";
import CalendarButton from "../../../components/form/CalendarButton";
// Store and Services
import {
  getAccountBalanceWebsites,
  getAccountBalanceAccounts,
} from "../../../services/accountBalanceService";
import { fetchAccountBalance, refresh, setFilters, setPage, setWeek, setDateRange } from "../../../redux/slices/accountBalanceSlice";
// Helpers
import {
  getWeeks
} from "../../../utils/helpers";

export default function AccountBalance() {
  const dispatch = useDispatch();
  const {accountBalance, pagination, loading, filters, page} = useSelector((state) => state.accountBalance);
  const [websites, setWebsites] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [weeks, setWeeks] = useState([]);
  
  useEffect(() => {

    setWeeks(getWeeks());

    getAccountBalanceWebsites().then((res) => {
      setWebsites(res.data.data);
    });

    getAccountBalanceAccounts().then((res) => {
      setAccounts(res.data.data);
    });

  }, []);

  useEffect(() => {
    dispatch(fetchAccountBalance());
  }, [dispatch, filters, page]);


  const formik = useFormik({
    initialValues: filters,
    validationSchema: Yup.object({
      website: Yup.string().nullable(),
      account_id: Yup.string().nullable(),
      start_date: Yup.date().nullable(),
      end_date: Yup.date().nullable(),
    }),
    onSubmit: (values) => {
      dispatch(setFilters(values));
    },
  });

  const handleClearFilter = () => {
    formik.resetForm();
    dispatch(refresh());
  };

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-12 gap-1 items-center">
          <div className="col-span-2">
            <DropDown  
              name="website"
              value={formik.values.website}
              options={websites}
              optionValue="value"
              optionLabel="label"
              placeholder="Select Website"
              handleChange={(e) =>
                formik.setFieldValue("website", e.target.value)
              }
            />
          </div>
          <div className="col-span-2">
            <DropDown
              name="account_id"
              value={formik.values.account_id}
              options={accounts}
              optionValue="value"
              optionLabel="label"
              placeholder="Select Account"
              handleChange={(e) =>
                formik.setFieldValue("account_id", e.target.value)
              }
            />
          </div>
          <div className="col-span-2">
            <input
              name="label"
              type="text"
              placeholder="Label"
              className="form-control"
              onChange={(e) =>
                formik.setFieldValue("label", e.target.value)
              }
            />
          </div>        
          <div className="col-span-1 flex justify-center">
            <button
              type="submit"
              className="bg-primary px-2 py-1 mr-2 text-white rounded"
            >
              <FontAwesomeIcon icon={faSearch} />
            </button>
            <button
              type="button"
              onClick={handleClearFilter}
              className="bg-primary px-2 py-1 text-white rounded"
            >
              <FontAwesomeIcon icon={faArrowRotateRight} />
            </button>
          </div>
         
        </div>
        <div className="grid grid-cols-12 gap-1 items-center mt-2">
          <div className="col-span-12 flex justify-end gap-2">
            <CalendarButton
              btnText="This week"
              handleSubmit={() => dispatch(setWeek(0))}
              selected={filters.week === 0}
            />
            <CalendarButton
              btnText="Last week"
              handleSubmit={() => dispatch(setWeek(1))}
              selected={filters.week === 1}
            />
            <CalendarButton
            btnText={"Two weeks ago"}
            handleSubmit={() => dispatch(setWeek(2))}
            selected={filters.week == 2}
          />
            <DropDown
              name="week"
              value={filters.week}
              options={weeks}
              optionValue="key"
              optionLabel="value"
              placeholder="Select Week"
              handleChange={(e) => dispatch(setWeek(e.target.value))}
            />
            <DateRangeCalendar
              onSelect={(e) => dispatch(setDateRange(e))}
              start_date={filters.start_date}
              end_date={filters.end_date}
            />
          </div>
        </div>
      </form>

      <hr className="border-black border-2 mt-2" />
      {loading ? (
        <TableLoader />
      ) : (
        <>
        <table className="table-auto w-full border-collapse mt-4 text-sm">
          <thead>
            <tr>
              <th>Website</th>
              <th className="whitespace-nowrap">Account ID</th>
              <th className="whitespace-nowrap">Label</th>
              <th>Week Start</th>
              <th>Week End</th>
              <th>Mon</th>
              <th>Tue</th>
              <th>Wed</th>
              <th>Thu</th>
              <th>Fri</th>
              <th>Sat</th>
              <th>Sun</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {accountBalance.map((item, index) => {
              const isTotalRow = !item.website && !item.account_id;
              return (
                <tr
                  key={index}
                  className={`
                    overflow-hidden whitespace-nowrap text-center 
                    border border-b-1 border-t-0 border-r-0 border-l-0
                    ${isTotalRow ? "bg-green-100" : ""}
                  `}
                >
                  <td className="overflow-hidden p-2">{item?.website}</td>
                  <td className="overflow-hidden p-2">{item?.account_id}</td>
                  <td className="overflow-hidden p-2">{item?.label}</td>
                  <td className="overflow-hidden p-2">{item?.week_start}</td>
                  <td className="overflow-hidden p-2">{item?.week_end}</td>
                  <td className="overflow-hidden p-2">{item?.monday}</td>
                  <td className="overflow-hidden p-2">{item?.tuesday}</td>
                  <td className="overflow-hidden p-2">{item?.wednesday}</td>
                  <td className="overflow-hidden p-2">{item?.thursday}</td>
                  <td className="overflow-hidden p-2">{item?.friday}</td>
                  <td className="overflow-hidden p-2">{item?.saturday}</td>
                  <td className="overflow-hidden p-2">{item?.sunday}</td>
                  <td className="overflow-hidden p-2">{item?.total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* <Pagination pagination={pagination} onPageChange={(page) => dispatch(setPage(page))} /> */}
        </>
      )}
    </div>
  );
}
